import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Sparkles, Printer, Download, CheckCircle2, XCircle, Loader2, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, StatusBadge, Loading } from "@/components/common";
import DocView from "@/components/DocView";
import { api, errMsg, API } from "@/lib/api";
import { fmtDay } from "@/lib/format";

function Shell({ doc, title, children }) {
  const b = doc.business;
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="bg-[#0F172A] text-white px-6 sm:px-10 py-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="font-display text-2xl font-semibold" data-testid="public-business-name">{b.name}</div>
              <div className="text-sm text-slate-300 mt-1">{[b.email, b.phone, b.address].filter(Boolean).join(" · ")}</div>
            </div>
            <div className="sm:text-right">
              <div className="text-xs uppercase tracking-wider text-slate-400">{title}</div>
              <div className="font-display text-xl">{doc.number}</div>
            </div>
          </div>
          <div className="px-6 sm:px-10 py-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div className="text-sm text-slate-600">Prepared for <span className="font-medium text-slate-900">{doc.client_name}</span> · {fmtDay(doc.created_at)}</div>
              <StatusBadge status={doc.payment_status || doc.status} testId="public-doc-status" />
            </div>
            <DocView doc={doc} />
            {children}
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-6 flex items-center gap-1 no-print"><Sparkles className="h-3 w-3" />Powered by CleanBoss</p>
      </div>
    </div>
  );
}

function usePublic(path) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [n, setN] = useState(0);
  useEffect(() => {
    api.get(path).then((r) => setDoc(r.data)).catch((e) => setError(errMsg(e)));
  }, [path, n]);
  return [doc, setDoc, error, () => setN((x) => x + 1)];
}

function NotFound({ error }) {
  return <div className="min-h-screen flex items-center justify-center p-6 text-slate-600" data-testid="public-doc-error">{error}</div>;
}

export function PublicEstimate() {
  const { token } = useParams();
  const [doc, setDoc, error] = usePublic(`/public/estimates/${token}`);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  if (error) return <NotFound error={error} />;
  if (!doc) return <div className="p-10"><Loading /></div>;

  const respond = async (action) => {
    if (!name.trim()) return setErr("Please type your full name to confirm.");
    setBusy(action); setErr("");
    try { const { data } = await api.post(`/public/estimates/${token}/respond`, { action, name, comment }); setDoc(data); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <Shell doc={doc} title="Estimate">
      {doc.valid_until && <p className="text-sm text-slate-500 mt-4">Valid until {fmtDay(doc.valid_until)}</p>}
      {doc.status === "sent" ? (
        <div className="mt-8 border-t border-slate-200 pt-6 space-y-4 no-print" data-testid="estimate-response-form">
          <h2 className="text-lg font-semibold">Your decision</h2>
          <Field label="Your full name" htmlFor="pe-name"><Input id="pe-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="public-name-input" /></Field>
          <Field label="Comment (optional)" htmlFor="pe-c"><Textarea id="pe-c" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="public-comment-input" /></Field>
          {err && <p className="text-sm text-red-700" role="alert" data-testid="public-respond-error">{err}</p>}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button className="h-12 flex-1" disabled={!!busy} onClick={() => respond("approve")} data-testid="approve-estimate-btn">{busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}Approve estimate</Button>
            <Button variant="outline" className="h-12 flex-1" disabled={!!busy} onClick={() => respond("decline")} data-testid="decline-estimate-btn">{busy === "decline" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}Decline</Button>
          </div>
        </div>
      ) : (
        <div className="mt-8 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm" data-testid="estimate-final-state">
          This estimate was {doc.status === "converted" ? "approved" : doc.status}{doc.responder_name ? ` by ${doc.responder_name}` : ""}{doc.responded_at ? ` on ${new Date(doc.responded_at).toLocaleDateString()}` : ""}. Thank you!
        </div>
      )}
    </Shell>
  );
}

function usePaymentReturn(token, refresh) {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState(params.get("canceled") ? "canceled" : params.get("session_id") ? "checking" : "");
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0, stop = false;
    const tick = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/public/invoices/${token}/payment-status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); setParams({}); refresh(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { setState("failed"); return; }
      if (++tries < 15) setTimeout(tick, 2000); else setState("pending");
    };
    tick();
    return () => { stop = true; };
  }, [params, setParams, token, refresh]);
  return state;
}

const PAY_MSG = {
  checking: ["border-sky-200 bg-sky-50 text-sky-900", "Confirming your payment with Stripe…"],
  paid: ["border-green-200 bg-green-50 text-green-900", "Payment received and confirmed. Thank you!"],
  canceled: ["border-slate-200 bg-slate-50 text-slate-700", "Payment canceled - you were not charged."],
  failed: ["border-red-200 bg-red-50 text-red-900", "The payment did not go through. You were not charged."],
  pending: ["border-amber-200 bg-amber-50 text-amber-900", "Still waiting for confirmation from Stripe. Refresh this page in a minute."],
};

export function PublicInvoice() {
  const { token } = useParams();
  const [doc, , error, refresh] = usePublic(`/public/invoices/${token}`);
  const payState = usePaymentReturn(token, refresh);
  const [busy, setBusy] = useState(false);
  const [payErr, setPayErr] = useState("");
  if (error) return <NotFound error={error} />;
  if (!doc) return <div className="p-10"><Loading /></div>;
  const pay = async () => {
    setBusy(true); setPayErr("");
    try { const { data } = await api.post(`/public/invoices/${token}/checkout`, { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { setPayErr(errMsg(e)); setBusy(false); }
  };
  return (
    <Shell doc={doc} title="Invoice">
      <p className="text-sm text-slate-500 mt-4">Due {fmtDay(doc.due_date)}</p>
      {payState && <div className={`mt-6 rounded-lg border p-4 text-sm no-print ${PAY_MSG[payState][0]}`} data-testid="payment-result">{PAY_MSG[payState][1]}</div>}
      {doc.online_payment_available && payState !== "checking" && (
        <div className="mt-6 no-print">
          <Button className="h-12 w-full sm:w-auto px-8" disabled={busy} onClick={pay} data-testid="pay-online-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}Pay {doc.balance ? `$${doc.balance.toFixed(2)}` : ""} online</Button>
          <p className="text-xs text-slate-500 mt-2">Secure card payment by Stripe. CleanBoss never sees or stores your card details.</p>
          {payErr && <p className="text-sm text-red-700 mt-2" role="alert" data-testid="pay-online-error">{payErr}</p>}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-6 no-print">
        <Button variant="outline" className="h-11" onClick={() => window.print()} data-testid="public-print-btn"><Printer className="h-4 w-4 mr-1" />Print</Button>
        <Button variant="outline" asChild className="h-11"><a href={`${API}/public/invoices/${token}/pdf`} data-testid="public-pdf-btn"><Download className="h-4 w-4 mr-1" />Download PDF</a></Button>
      </div>
      {doc.balance > 0 && doc.payment_status !== "void" && !doc.online_payment_available && (
        <div className="mt-6 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 no-print" data-testid="payment-instructions">
          Online card payment isn't available for this invoice. Please contact {doc.business.name}{doc.business.phone ? ` at ${doc.business.phone}` : ""} to pay by cash, check or bank transfer.
        </div>
      )}
    </Shell>
  );
}
